import CheckIcon from '@mui/icons-material/Check';
import {
    Box,
    Button,
    Collapse,
    Grid,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import React, { useRef, useState } from 'react';
import { publicCardSx, publicColors, publicContainerSx, publicH2Sx, publicSectionSx, publicSubtitleSx } from './publicTheme';

const plans = [
    {
        id: 'a6c4439a-f47c-49ab-820a-2a021298d909',
        name: 'Começando',
        price: '0,00',
        pain: 'Você vende, mas não tem histórico nem visão do que deu certo.',
        features: ['Registro de vendas', 'Relatórios essenciais', 'Suporte por e-mail'],
        highlight: true,
        enabled: true,
    },
    {
        id: 'f545c6fd-a736-4c71-89be-9d566aa0bdef',
        name: 'Crescendo',
        price: '29,90',
        pain: 'Precisa precificar e controlar encomendas sem perder margem.',
        features: ['Precificação avançada', 'Gestão de pedidos', '7 dias grátis'],
        highlight: false,
        enabled: false,
    },
    {
        id: '4c073b67-13f4-4e3f-8714-5311bfb50019',
        name: 'Profissional',
        price: '49,90',
        pain: 'Operação cheia exige orçamento, financeiro e métricas para decidir.',
        features: ['Orçamentos formais', 'Módulo financeiro', '14 dias grátis'],
        highlight: false,
        enabled: false,
    },
];

const comparisonRows = [
    ['Dashboard e métricas de jobs', 'Básico', 'Completo', 'Completo avançado'],
    ['Integração de impressoras', '—', '✔', '✔'],
    ['Calculadora / precificação', 'Simples', 'Avançada', 'Avançada'],
    ['Módulo de vendas', '✔', '✔', '✔'],
    ['Orçamentos', '—', '—', '✔'],
    ['Financeiro (entradas/saídas)', '—', '—', '✔'],
    ['Suporte', 'E-mail (5 dias úteis)', 'E-mail (2 dias úteis)', 'WhatsApp comercial'],
];

const Pricing = () => {
    const [isTableVisible, setIsTableVisible] = useState(false);
    const tableRef = useRef(null);

    const scrollToRegister = (planId) => {
        const target = document.getElementById('registrationForm');
        const headerHeight = document.querySelector('header')?.offsetHeight || 72;
        if (target) {
            window.scrollTo({ top: target.offsetTop - headerHeight, behavior: 'smooth' });
            const planInput = document.getElementById('planId');
            if (planInput) {
                planInput.value = planId;
            }
        }
    };

    return (
        <Box id="precos" component="section" sx={{ ...publicSectionSx, bgcolor: publicColors.surface }}>
            <Box sx={publicContainerSx}>
                <Typography component="h2" sx={{ ...publicH2Sx, textAlign: 'center' }}>
                    Planos para cada estágio da sua gestão
                </Typography>
                <Typography sx={{ ...publicSubtitleSx, textAlign: 'center', mb: 5 }}>
                    Comece medindo vendas sem custo. Evolua quando precisar de estatísticas e ferramentas
                    para decidir preço, capacidade e investimento em produção.
                </Typography>
                <Grid container spacing={3} justifyContent="center">
                    {plans.map((plan) => (
                        <Grid item xs={12} md={4} key={plan.id}>
                            <Box
                                sx={{
                                    ...publicCardSx,
                                    borderColor: plan.highlight ? publicColors.primary : publicColors.border,
                                    borderWidth: plan.highlight ? 2 : 1,
                                }}
                            >
                                <Typography variant="overline" sx={{ color: publicColors.inkSoft, fontWeight: 600 }}>
                                    {plan.name}
                                </Typography>
                                <Typography sx={{ fontSize: '2rem', fontWeight: 800, color: publicColors.ink, my: 1 }}>
                                    R$ {plan.price}
                                    <Typography component="span" variant="body2" sx={{ color: publicColors.inkSoft }}>
                                        {' '}
                                        /mês
                                    </Typography>
                                </Typography>
                                <Typography variant="body2" sx={{ color: publicColors.inkMuted, mb: 2, minHeight: 48 }}>
                                    {plan.pain}
                                </Typography>
                                <List dense disablePadding>
                                    {plan.features.map((f) => (
                                        <ListItem key={f} disableGutters sx={{ py: 0.25 }}>
                                            <ListItemIcon sx={{ minWidth: 32 }}>
                                                <CheckIcon sx={{ fontSize: 20, color: publicColors.accent }} />
                                            </ListItemIcon>
                                            <ListItemText primary={f} primaryTypographyProps={{ variant: 'body2' }} />
                                        </ListItem>
                                    ))}
                                </List>
                                <Button
                                    fullWidth
                                    variant={plan.enabled ? 'contained' : 'outlined'}
                                    disabled={!plan.enabled}
                                    onClick={() => plan.enabled && scrollToRegister(plan.id)}
                                    sx={{
                                        mt: 3,
                                        fontWeight: 600,
                                        boxShadow: 'none',
                                        ...(plan.enabled
                                            ? {
                                                bgcolor: publicColors.primary,
                                                '&:hover': { bgcolor: publicColors.primaryDark, boxShadow: 'none' },
                                            }
                                            : {
                                                borderColor: publicColors.border,
                                                color: publicColors.inkSoft,
                                            }),
                                    }}
                                >
                                    {plan.enabled ? 'Começar grátis' : 'Em breve'}
                                </Button>
                            </Box>
                        </Grid>
                    ))}
                </Grid>
                <Box textAlign="center" sx={{ mt: 4 }}>
                    <Button
                        variant="text"
                        onClick={() => setIsTableVisible(!isTableVisible)}
                        sx={{ color: publicColors.primary, fontWeight: 600 }}
                    >
                        {isTableVisible ? 'Ocultar comparativo' : 'Ver comparativo de recursos'}
                    </Button>
                </Box>
                <Collapse in={isTableVisible}>
                    <TableContainer
                        ref={tableRef}
                        sx={{
                            mt: 3,
                            border: `1px solid ${publicColors.border}`,
                            borderRadius: 2,
                            bgcolor: publicColors.surface,
                        }}
                    >
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ bgcolor: publicColors.surfaceAlt }}>
                                    <TableCell sx={{ fontWeight: 700 }}>Recurso</TableCell>
                                    {plans.map((p) => (
                                        <TableCell key={p.id} align="center" sx={{ fontWeight: 700 }}>
                                            {p.name}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {comparisonRows.map(([label, ...cols]) => (
                                    <TableRow key={label}>
                                        <TableCell>{label}</TableCell>
                                        {cols.map((cell, i) => (
                                            <TableCell key={plans[i].id} align="center">
                                                {cell}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Collapse>
                <Box
                    sx={{
                        mt: 5,
                        p: 3,
                        borderRadius: 2,
                        bgcolor: publicColors.surfaceAlt,
                        border: `1px solid ${publicColors.border}`,
                        textAlign: 'center',
                    }}
                >
                    <Typography sx={{ fontWeight: 600, color: publicColors.ink, mb: 1 }}>
                        Suporte humano por e-mail e WhatsApp
                    </Typography>
                    <Typography variant="body2" sx={{ color: publicColors.inkMuted }}>
                        Planos pagos incluem precificação e evolução contínua de métricas para sua operação 3D.
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
};

export default Pricing;
